import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Alert,
  LinearProgress,
  Box,
  CircularProgress
} from '@mui/material';
import { questionnaireApi, QuestionnaireResponse } from '../services/api';
import { 
  QuestionRenderer
} from '../components/QuestionRenderer';
import styles from './Questionnaire.module.css';

// Define Question type locally until package is available
interface Question {
  id: string;
  type: string;
  question: string;
  required?: boolean;
  options?: string[];
}

const Questionnaire: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [answer, setAnswer] = useState<any>('');
  const [progress, setProgress] = useState<{ current: number; total: number; answered?: number }>({ current: 0, total: 0, answered: 0 });
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [multipleChoiceAnswers, setMultipleChoiceAnswers] = useState<string[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [initializationStarted, setInitializationStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);

  useEffect(() => {
    if (sessionId) {
      loadSession();
    } else {
      navigate('/404', { replace: true });
    }
    
    // Cleanup function to stop polling when component unmounts
    return () => {
      setIsPolling(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadSession = async () => {
    if (!sessionId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await questionnaireApi.getSession(sessionId);
      
      // Check if session is still generating questions (new non-blocking flow)
      if ((response as any).status === 'generating' || (response as any).needsInitialization) {
        console.log('Session is generating or needs initialization...');
        
        // Check if we should retry (backend is checking run status)
        if ((response as any).retry) {
          console.log('Backend indicates retry, polling for questions...');
          // Show generating message to user
          setInitializing(true);
          // Start polling with shorter interval since backend is checking status
          startQuestionPolling();
        } else if ((response as any).status === 'generating') {
          console.log('Questions are being generated, starting status check polling...');
          startStatusPolling();
        } else {
          // Old flow - needs initialization
          await initializeSession();
        }
      } else {
        handleQuestionnaireResponse(response);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        navigate('/404', { replace: true });
      } else {
        setError(err.message || 'Failed to load questionnaire. Please try again.');
        setLoading(false);
      }
    }
  };

  const initializeSession = async () => {
    if (!sessionId) return;
    
    // Prevent duplicate initialization
    if (initializationStarted || isPolling) {
      console.log('Initialization already in progress, skipping...');
      return;
    }
    
    try {
      setInitializing(true);
      setInitializationStarted(true);
      
      // Start initialization - only call once
      const initResponse = await questionnaireApi.initializeSession(sessionId);
      console.log('Initialize response:', initResponse);
      
      // If session has questions already, just reload
      if ((initResponse as any).hasQuestions) {
        console.log('Session already has questions, reloading...');
        setInitializing(false);
        setInitializationStarted(false);
        await loadSession();
        return;
      }
      
      // If already initialized or initializing, start polling
      if ((initResponse as any).status === 'in_progress' || (initResponse as any).status === 'initializing') {
        console.log('Session initialization triggered, starting polling...');
        startPolling();
        return;
      }
      
    } catch (err: any) {
      console.error('Failed to start initialization:', err);
      setError(err.message || 'Failed to initialize questionnaire. Please try again.');
      setLoading(false);
      setInitializing(false);
      setInitializationStarted(false);
    }
  };

  const startPolling = () => {
    if (isPolling) {
      console.log('Already polling, skipping...');
      return;
    }
    
    setIsPolling(true);
    let pollCount = 0;
    const maxPolls = 30; // 30 * 2 seconds = 60 seconds max
    
    console.log('Starting to poll for session readiness...');
    const pollInterval = setInterval(async () => {
      pollCount++;
      
      if (pollCount > maxPolls) {
        console.error('Polling timeout reached');
        clearInterval(pollInterval);
        setIsPolling(false);
        setInitializing(false);
        setLoading(false);
        setError('Session initialization is taking longer than expected. Please refresh the page.');
        return;
      }
      
      try {
        const response = await questionnaireApi.getSession(sessionId!);
        console.log(`Poll attempt ${pollCount}: `, response);
        
        // Check if still needs initialization (questions not ready yet)
        if ((response as any).needsInitialization) {
          console.log('Questions still being generated...');
          // Check if message indicates re-initialization needed
          if ((response as any).message?.includes('needs to be restored')) {
            console.log('Session needs re-initialization, stopping poll');
            clearInterval(pollInterval);
            setIsPolling(false);
            setInitializing(false);
            setLoading(false);
            setError('Session data was lost. Please refresh the page to re-initialize.');
            return;
          }
          return; // Continue polling
        }
        
        // Check if session is ready with questions
        if (response.question) {
          console.log('Session ready with questions!');
          clearInterval(pollInterval);
          setIsPolling(false);
          setInitializing(false);
          setLoading(false);
          setInitializationStarted(false); // Reset for future use
          handleQuestionnaireResponse(response);
        } else if (response.isComplete) {
          console.log('Session already complete');
          clearInterval(pollInterval);
          setIsPolling(false);
          setInitializing(false);
          setLoading(false);
          setInitializationStarted(false); // Reset for future use
          handleQuestionnaireResponse(response);
        }
      } catch (err: any) {
        console.error('Polling error:', err);
        // Continue polling unless it's a 404
        if (err.response?.status === 404) {
          clearInterval(pollInterval);
          setIsPolling(false);
          navigate('/404', { replace: true });
        }
      }
    }, 2000); // Poll every 2 seconds
  };

  const startQuestionPolling = () => {
    if (isPolling) {
      console.log('Already polling, skipping...');
      return;
    }
    
    setIsPolling(true);
    setInitializing(true);
    let pollCount = 0;
    const maxPolls = 15; // 15 * 2 seconds = 30 seconds max
    
    console.log('Starting to poll for questions (backend checking run status)...');
    const pollInterval = setInterval(async () => {
      pollCount++;
      
      if (pollCount > maxPolls) {
        console.error('Question polling timeout reached');
        clearInterval(pollInterval);
        setIsPolling(false);
        setInitializing(false);
        setLoading(false);
        setError('Question generation is taking longer than expected. Please refresh the page.');
        return;
      }
      
      try {
        // Call getSession which now checks run status internally
        const response = await questionnaireApi.getSession(sessionId!);
        console.log(`Question poll attempt ${pollCount}: `, response);
        
        // Check if we have a question now
        if (response.question) {
          console.log('Questions are ready!');
          clearInterval(pollInterval);
          setIsPolling(false);
          setInitializing(false);
          setLoading(false);
          handleQuestionnaireResponse(response);
        } else if ((response as any).error) {
          console.error('Question generation failed:', (response as any).error);
          clearInterval(pollInterval);
          setIsPolling(false);
          setInitializing(false);
          setLoading(false);
          setError((response as any).error || 'Failed to generate questions. Please try again.');
        }
        // If still generating with retry flag, continue polling
      } catch (err: any) {
        console.error('Question polling error:', err);
        if (err.response?.status === 500) {
          // Server error, stop polling
          clearInterval(pollInterval);
          setIsPolling(false);
          setInitializing(false);
          setLoading(false);
          setError('Failed to generate questions. Please try again.');
        }
      }
    }, 2000); // Poll every 2 seconds
  };

  const startStatusPolling = () => {
    if (isPolling) {
      console.log('Already polling, skipping...');
      return;
    }
    
    setIsPolling(true);
    setInitializing(true);
    let pollCount = 0;
    const maxPolls = 30; // 30 * 2 seconds = 60 seconds max
    
    console.log('Starting to poll for assistant run status...');
    const pollInterval = setInterval(async () => {
      pollCount++;
      
      if (pollCount > maxPolls) {
        console.error('Status polling timeout reached');
        clearInterval(pollInterval);
        setIsPolling(false);
        setInitializing(false);
        setLoading(false);
        setError('Question generation is taking longer than expected. Please refresh the page.');
        return;
      }
      
      try {
        // Check the run status
        const statusResponse = await questionnaireApi.checkRunStatus(sessionId!);
        console.log(`Status poll attempt ${pollCount}: `, statusResponse);
        
        if (statusResponse.status === 'ready') {
          console.log('Questions are ready!');
          clearInterval(pollInterval);
          setIsPolling(false);
          setInitializing(false);
          // Load the session now that questions are ready
          await loadSession();
        } else if (statusResponse.status === 'error') {
          console.error('Question generation failed:', statusResponse.error);
          clearInterval(pollInterval);
          setIsPolling(false);
          setInitializing(false);
          setLoading(false);
          setError(statusResponse.error || 'Failed to generate questions. Please try again.');
        }
        // If status is 'generating', continue polling
      } catch (err: any) {
        console.error('Status polling error:', err);
        // If the status endpoint fails, fall back to checking the session
        try {
          const response = await questionnaireApi.getSession(sessionId!);
          if (response.state !== 'generating' && response.question) {
            console.log('Session ready (fallback check)');
            clearInterval(pollInterval);
            setIsPolling(false);
            setInitializing(false);
            setLoading(false);
            handleQuestionnaireResponse(response);
          }
        } catch (fallbackErr) {
          console.error('Fallback check failed:', fallbackErr);
        }
      }
    }, 2000); // Poll every 2 seconds
  };

  const handleQuestionnaireResponse = (response: QuestionnaireResponse) => {
    if (response.isComplete) {
      navigate(`/completed/${sessionId}`);
      return;
    }
    
    setCurrentQuestion(response.question || null);
    if (response.progress) {
      setProgress(response.progress);
    }
    
    // Set navigation state
    if (response.currentIndex !== undefined) {
      setCurrentQuestionIndex(response.currentIndex);
      setCanGoBack(response.canGoBack || false);
      setCanGoForward(response.canGoForward || false);
    }
    
    // If there's an existing answer, populate it
    if (response.hasAnswer && response.existingAnswer !== undefined) {
      if (response.question?.type === 'multiple-choice' && Array.isArray(response.existingAnswer)) {
        setMultipleChoiceAnswers(response.existingAnswer);
        setAnswer('');
      } else if (response.question?.type === 'body-map') {
        // Body-map expects an array
        setAnswer(Array.isArray(response.existingAnswer) ? response.existingAnswer : []);
        setMultipleChoiceAnswers([]);
      } else if (response.question?.type === 'pain-scale') {
        // Pain-scale expects a number
        setAnswer(typeof response.existingAnswer === 'number' ? response.existingAnswer : 0);
        setMultipleChoiceAnswers([]);
      } else {
        setAnswer(response.existingAnswer);
        setMultipleChoiceAnswers([]);
      }
    } else {
      // Set default values based on question type
      if (response.question?.type === 'body-map') {
        setAnswer([]);
      } else if (response.question?.type === 'pain-scale') {
        setAnswer(0);
      } else {
        setAnswer('');
      }
      setMultipleChoiceAnswers([]);
    }
    
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!currentQuestion) return;

    // Check if answer is required and empty
    const isAnswerEmpty = () => {
      if (currentQuestion.type === 'multiple-choice') {
        return multipleChoiceAnswers.length === 0;
      } else if (currentQuestion.type === 'body-map') {
        return !answer || (Array.isArray(answer) && answer.length === 0);
      } else if (currentQuestion.type === 'pain-scale') {
        return answer === '' || answer === null || answer === undefined;
      } else {
        return !answer;
      }
    };

    if (currentQuestion.required && isAnswerEmpty()) {
      setError('This question is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      let answerValue: any;
      if (currentQuestion.type === 'multiple-choice') {
        answerValue = multipleChoiceAnswers;
      } else if (currentQuestion.type === 'body-map') {
        answerValue = Array.isArray(answer) ? answer : [];
      } else if (currentQuestion.type === 'pain-scale') {
        answerValue = typeof answer === 'number' ? answer : parseInt(answer) || 0;
      } else {
        answerValue = answer;
      }
      
      // Use update API to handle both new and existing answers
      const response = await questionnaireApi.updateAnswer({
        questionId: currentQuestion.id,
        answer: answerValue,
        sessionId: sessionId!,
        questionIndex: currentQuestionIndex
      });
      
      handleQuestionnaireResponse(response);
    } catch (err: any) {
      setError(err.message || 'Failed to submit answer. Please try again.');
      setLoading(false);
    }
  };

  const handlePrevious = async () => {
    if (!canGoBack || currentQuestionIndex <= 0) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await questionnaireApi.getQuestionByIndex(sessionId!, currentQuestionIndex - 1);
      handleQuestionnaireResponse(response);
    } catch (err: any) {
      setError(err.message || 'Failed to go to previous question.');
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (!canGoForward || currentQuestionIndex >= progress.total - 1) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await questionnaireApi.getQuestionByIndex(sessionId!, currentQuestionIndex + 1);
      handleQuestionnaireResponse(response);
    } catch (err: any) {
      setError(err.message || 'Failed to go to next question.');
      setLoading(false);
    }
  };

  const handleMultipleChoiceChange = (option: string) => {
    setMultipleChoiceAnswers(prev => {
      if (prev.includes(option)) {
        return prev.filter(item => item !== option);
      }
      return [...prev, option];
    });
  };

  const renderQuestionInput = () => {
    if (!currentQuestion) return null;

    // For multiple-choice, use the multipleChoiceAnswers state
    const currentValue = currentQuestion.type === 'multiple-choice' 
      ? multipleChoiceAnswers 
      : answer;

    return (
      <QuestionRenderer
        question={currentQuestion}
        value={currentValue}
        onChange={(value) => {
          if (currentQuestion.type === 'multiple-choice') {
            setMultipleChoiceAnswers(Array.isArray(value) ? value : []);
            setAnswer(value); // Also set answer for consistency
          } else {
            setAnswer(value);
          }
        }}
        readonly={loading}
        hasNextButton={false} // We handle the Next button separately
      />
    );
  };


  if ((loading && !currentQuestion) || initializing) {
    return (
      <Container maxWidth="sm" className={styles.container}>
        <Paper className={styles.paper} elevation={3}>
          <Box className={styles.logoContainer}>
            <img src="/favicon.svg" alt="Tibbbi Logo" className={styles.logo} />
          </Box>
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress size={48} sx={{ mb: 3 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {initializing ? 'Preparing your personalized health assessment...' : 'Loading...'}
            </Typography>
            {initializing && (
              <>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1, px: 3 }}>
                  Our AI is analyzing your health concern and generating relevant questions specifically for you.
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1, px: 3 }}>
                  This usually takes 10-20 seconds...
                </Typography>
              </>
            )}
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" className={styles.container}>
      <Paper className={styles.paper} elevation={3}>
        <Box className={styles.logoContainer}>
          <img src="/favicon.svg" alt="Tibbbi Logo" className={styles.logo} />
        </Box>
        
        {progress.total > 0 && (
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="textSecondary">
                Progress
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Question {progress.current} of {progress.total}
                {progress.answered !== undefined && progress.answered > 0 && (
                  <span> • {progress.answered} answered</span>
                )}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(progress.answered || progress.current) / progress.total * 100} 
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {currentQuestion && (
          <>
            <Typography variant="h6" gutterBottom className={styles.question}>
              {currentQuestion.question}
            </Typography>

            <Box mb={3}>
              {renderQuestionInput()}
            </Box>

            <Box display="flex" gap={2}>
              {canGoBack && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handlePrevious}
                  disabled={loading}
                  size="large"
                  sx={{ flex: 1 }}
                >
                  Previous
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
                size="large"
                sx={{ flex: canGoBack ? 2 : 1 }}
                fullWidth={!canGoBack}
              >
                {loading ? <CircularProgress size={24} /> : 'Next'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default Questionnaire;